import type { CreateMessageInput, Message, MessageListParams, UpdateMessageInput } from '@/domain/message.types'
import { apiClient } from './api.client'

/** One item's outcome from `POST /messages/translations/batch` — see `messageApi.requestTranslationBatch`. */
export interface MessageTranslationBatchResultItem {
	messageId: string
	targetLocales: string[]
	targets?: Message[]
	error?: string
}

/**
 * Message API client — mirrors the message-service REST shape. List filters
 * (search, status, languageIso, messageType, translationGroupId) are sent as a
 * JSON `filter` query param, which the backend's parseQueryParams expects.
 */
const unwrap = <T>(data: { data: T }): T => data.data

/**
 * `page`/`limit` are top-level query params on the backend's `parseQueryParams`,
 * NOT filter keys — folding them into the JSON `filter` would make the list
 * query an equality match on non-existent columns instead of paginating.
 */
const toQueryParams = (params?: MessageListParams): Record<string, string> | undefined => {
	if (!params) return undefined
	const { page, limit, ...filters } = params
	const filter = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''))
	const query: Record<string, string> = {}
	if (Object.keys(filter).length > 0) query.filter = JSON.stringify(filter)
	if (page !== undefined) query.page = String(page)
	if (limit !== undefined) query.limit = String(limit)
	return Object.keys(query).length > 0 ? query : undefined
}

export const messageApi = {
	list: (params?: MessageListParams) =>
		apiClient.get<{ data: Message[] }>('/messages', { params: toQueryParams(params) }).then((r) => unwrap(r.data)),
	get: (id: string) => apiClient.get<{ data: Message }>(`/messages/${id}`).then((r) => unwrap(r.data)),
	create: (body: CreateMessageInput) =>
		apiClient.post<{ data: Message }>('/messages', body).then((r) => unwrap(r.data)),
	update: (id: string, patch: UpdateMessageInput) =>
		apiClient.put<{ data: Message }>(`/messages/${id}`, patch).then((r) => unwrap(r.data)),
	remove: (id: string) => apiClient.delete<void>(`/messages/${id}`).then((r) => r.data),
	requestTranslation: (id: string, targetLocales: string[]) =>
		apiClient.post<{ data: Message[] }>(`/messages/${id}/translations`, { targetLocales }).then((r) => unwrap(r.data)),
	// Per-item success/failure, never all-or-nothing — see design.md D4 of the
	// campaign-message-translations change. `error` items are a best-effort
	// synchronous-error signal only; callers must reconcile true state via
	// polling (useCampaignMessages / the translation-group hooks), never by
	// trusting this response as final.
	requestTranslationBatch: (items: { messageId: string; targetLocales: string[] }[]) =>
		apiClient
			.post<{ data: MessageTranslationBatchResultItem[] }>('/messages/translations/batch', { items })
			.then((r) => unwrap(r.data)),
}
